import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, ChevronRight, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.patients || d?.results || []);

export default function Patients() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", access_code: "", category: "", access_expires_at: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setPatients(asList(await api.patients())); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.username || !form.access_code) { toast.error("Username e codice obbligatori"); return; }
    setSaving(true);
    try {
      await api.createPatient({
        username: form.username, access_code: form.access_code,
        category: form.category || undefined,
        access_expires_at: form.access_expires_at ? new Date(form.access_expires_at).toISOString() : undefined,
      });
      toast.success("Paziente creato");
      setForm({ username: "", access_code: "", category: "", access_expires_at: "" });
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const removePatient = async (id, name) => {
    try {
      await api.deletePatient(id);
      toast.success(`Paziente ${name} eliminato`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = patients.filter((p) =>
    (p.full_name || p.username || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        title="Pazienti"
        subtitle="Gestisci e monitora i tuoi pazienti."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="create-patient-button"><Plus className="mr-2 h-4 w-4" /> Nuovo paziente</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Crea nuovo paziente</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1.5" data-testid="patient-username-input" /></div>
                <div><Label>Codice di accesso</Label><Input value={form.access_code} onChange={(e) => setForm({ ...form, access_code: e.target.value })} className="mt-1.5" data-testid="patient-code-input" /></div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="patient-category-select"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scoliosi">Scoliosi</SelectItem>
                      <SelectItem value="post-operatorio">Post-operatorio</SelectItem>
                      <SelectItem value="riabilitazione">Riabilitazione</SelectItem>
                      <SelectItem value="mantenimento">Mantenimento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Scadenza accesso</Label><Input type="date" value={form.access_expires_at} onChange={(e) => setForm({ ...form, access_expires_at: e.target.value })} className="mt-1.5" data-testid="patient-expiry-input" /></div>
              </div>
              <DialogFooter><Button onClick={create} disabled={saving} data-testid="save-patient-button">{saving ? "Creazione..." : "Crea paziente"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca paziente..." className="pl-9" data-testid="patient-search" />
      </div>

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> :
        filtered.length === 0 ? <EmptyState title="Nessun paziente" subtitle="Crea il tuo primo paziente." /> : (
          <div className="grid gap-3" data-testid="patients-list">
            {filtered.map((p, i) => {
              const id = p.id || p._id;
              const name = p.full_name || p.username;
              const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
              return (
                <Card key={id || i} className="cursor-pointer border-border/70 transition-all hover:border-primary/40 hover:shadow-sm"
                  onClick={() => id && navigate(`/physio/patients/${id}`)} data-testid={`patient-row-${i}`}>
                  <CardContent className="flex items-center gap-4 p-4">
                    <Avatar className="h-11 w-11 bg-primary/15"><AvatarFallback className="bg-transparent font-semibold text-primary">{initials}</AvatarFallback></Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{name}</p>
                      <p className="text-xs text-muted-foreground">@{p.username}</p>
                    </div>
                    {p.category && <Badge variant="secondary">{p.category}</Badge>}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={(e) => e.stopPropagation()} data-testid={`delete-patient-${i}`} aria-label={`Elimina ${name}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader><AlertDialogTitle>Eliminare il paziente?</AlertDialogTitle><AlertDialogDescription>Il profilo di {name} verrà eliminato definitivamente insieme ai suoi dati. Questa azione non può essere annullata.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => removePatient(id, name)}>Elimina</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
}
